import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ShieldCheck, Cube, LinkSimple, FileLock, ChartBar, MagnifyingGlass,
    UserCircleCheck, Lightning, ArrowRight, Eye, Stack, Lock, Brain,
    EnvelopeSimple
} from "@phosphor-icons/react";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { SalesInquiryDialog } from "../components/SalesInquiryDialog";
import api from "../lib/api";

function shortHash(h) {
    if (!h) return "—";
    return `${h.slice(0, 7)}…${h.slice(-4)}`;
}

function formatRelative(iso) {
    if (!iso) return "—";
    try {
        const dt = new Date(iso).getTime();
        const diffSec = Math.max(0, Math.floor((Date.now() - dt) / 1000));
        if (diffSec < 60) return `${diffSec}s ago`;
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
        return `${Math.floor(diffSec / 86400)}d ago`;
    } catch { return "—"; }
}

function HeroChain() {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        api.get("/blockchain/summary").then(({ data }) => { if (alive) setData(data); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const genesis = data?.genesis;
    const latest = data?.latest;
    const total = data?.total ?? 0;
    const gap = Math.max(0, total - (latest ? 2 : 1));

    // Fallback placeholders if backend is unreachable
    const g = genesis || { index: 0, hash: "9f86d0818884c…3a2c", timestamp: null };
    const L = latest || (total === 1 ? null : { index: Math.max(1, total - 1), hash: "ef2d127de1b91…d1a7", timestamp: null });

    return (
        <div className="relative space-y-2">
            {/* Genesis block */}
            <BlockTile idx={`0x${(g.index ?? 0).toString(16).padStart(2, "0")}`} label="GENESIS" hash={shortHash(g.hash)} time={g.timestamp ? new Date(g.timestamp).toUTCString().slice(17, 25) : "00:00:00"} highlight={false} />

            {/* Gap indicator + count of intermediate blocks */}
            {L && (
                <div className="relative flex items-center justify-center gap-2 py-1" data-testid="chain-gap">
                    <div className="h-px flex-1 bg-civic-100" />
                    <span className="hash-mono text-[10px] text-slate-500 px-2 py-0.5 rounded-sm bg-slate-50 border border-slate-200">
                        + {gap.toLocaleString()} {gap === 1 ? "block" : "blocks"}
                    </span>
                    <div className="h-px flex-1 bg-civic-100" />
                </div>
            )}

            {/* Latest block */}
            {L && (
                <BlockTile idx={`0x${(L.index ?? 0).toString(16).padStart(2, "0")}`} label="VOTE" hash={shortHash(L.hash)} time={formatRelative(L.timestamp)} highlight />
            )}
        </div>
    );
}

function BlockTile({ idx, label, hash, time, highlight }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className={`relative bg-white border-2 ${highlight ? "border-civic-600" : "border-civic-100"} rounded-md px-3 py-2 shadow-[0_2px_0_0_rgba(15,82,186,0.06)]`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <Cube size={16} weight={highlight ? "fill" : "duotone"} className="text-civic-600" />
                    <div>
                        <div className="hash-mono text-[11px] text-slate-500 leading-tight">block {idx}</div>
                        <div className="overline text-[10px] text-civic-700">{label}</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="hash-mono text-[11px] text-slate-900">{hash}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{time}</div>
                </div>
            </div>
        </motion.div>
    );
}

function FeatureCard({ icon: Icon, title, body, testid }) {
    return (
        <div
            data-testid={testid}
            className="group relative bg-white border border-slate-200 p-6 rounded-md transition-all hover:-translate-y-1 hover:shadow-[0_8px_24px_-12px_rgba(15,82,186,0.25)] hover:border-civic-200"
        >
            <div className="absolute top-6 right-6 overline text-slate-300 group-hover:text-civic-600 transition-colors">
                <Icon size={18} weight="bold" />
            </div>
            <Icon size={28} weight="duotone" className="text-civic-600 mb-4" />
            <h3 className="font-heading font-bold text-lg mb-2 text-slate-900">{title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
        </div>
    );
}

function PrimerCard({ n, icon: Icon, title, body }) {
    return (
        <div className="bg-white border border-slate-200 rounded-md p-4 hover:border-civic-200 transition-colors">
            <div className="flex items-start justify-between">
                <div className="h-9 w-9 grid place-items-center rounded-md bg-civic-50 border border-civic-100">
                    <Icon size={18} weight="duotone" className="text-civic-700" />
                </div>
                <span className="hash-mono text-[10px] text-slate-400">{n}</span>
            </div>
            <h3 className="font-heading font-bold text-base mt-3 text-slate-900">{title}</h3>
            <p className="mt-1.5 text-[13px] text-slate-600 leading-snug">{body}</p>
        </div>
    );
}

function CryptoCard({ tag, title, body, testid }) {    return (
        <div
            data-testid={testid}
            className="bg-white border border-slate-200 rounded-md p-5 hover:border-civic-200 hover:shadow-[0_4px_18px_-12px_rgba(15,82,186,0.25)] transition-all"
        >
            <div className="flex items-baseline gap-3 flex-wrap">
                <span className="hash-mono text-[10px] uppercase tracking-widest text-civic-700 bg-civic-50 border border-civic-100 px-2 py-0.5 rounded-sm">{tag}</span>
                <h3 className="font-heading font-bold text-lg text-slate-900">{title}</h3>
            </div>
            <div className="mt-3 text-sm text-slate-700 leading-relaxed">{body}</div>
        </div>
    );
}

export default function Landing() {
    const [salesOpen, setSalesOpen] = useState(false);
    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-slate-200">
                <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-10 relative">
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center gap-2 border border-civic-200 bg-civic-50 text-civic-700 px-2.5 py-0.5 rounded-sm overline text-[10px] mb-3"
                                data-testid="hero-badge"
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-soft" />
                                Live verifiable ledger · v1.0
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.05 }}
                                className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-[1.1] text-slate-900"
                            >
                                Votes you can <span className="text-civic-600">verify</span>.
                                <br />
                                Voters you can't <span className="line-through decoration-civic-600 decoration-2">trace</span>.
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="mt-3 text-xs text-slate-600 max-w-xl leading-relaxed"
                            >
                                E-VoteVerify.com is a hash-chained ledger of anonymous votes. Voter identities are kept in a separate book and reconciled by totals only — preserving secrecy of the ballot while making every count publicly auditable.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.15 }}
                                className="mt-4 flex flex-wrap gap-2"
                            >
                                <Link to="/login/voter">
                                    <Button size="sm" className="bg-civic-600 hover:bg-civic-700 text-white rounded-md h-8 text-xs" data-testid="hero-cta-vote">
                                        Cast Your Vote <ArrowRight size={14} className="ml-1.5" weight="bold" />
                                    </Button>
                                </Link>
                                <Link to="/explorer">
                                    <Button size="sm" variant="outline" className="border-slate-300 rounded-md h-8 text-xs" data-testid="hero-cta-explorer">
                                        <Stack size={14} className="mr-1.5" /> Open Explorer
                                    </Button>
                                </Link>
                            </motion.div>

                            <div className="mt-5 grid grid-cols-3 gap-3 sm:gap-6 max-w-md" data-testid="hero-stats">
                                <div>
                                    <div className="overline text-[10px] text-slate-500">Hash</div>
                                    <div className="hash-mono text-xs text-slate-900 mt-0.5">SHA-256</div>
                                </div>
                                <div>
                                    <div className="overline text-[10px] text-slate-500">Anonymity</div>
                                    <div className="text-xs font-semibold text-slate-900 mt-0.5">Sealed</div>
                                </div>
                                <div>
                                    <div className="overline text-[10px] text-slate-500">Audit</div>
                                    <div className="text-xs font-semibold text-emerald-700 mt-0.5">Public</div>
                                </div>
                            </div>
                        </div>

                        {/* Block visualization — genesis + latest only (chain may have thousands) */}
                        <div className="lg:col-span-5 relative">
                            <HeroChain />
                        </div>
                    </div>
                </div>
            </section>

            {/* What is a blockchain? — plain-English primer */}
            <section className="border-b border-slate-200 bg-slate-50/60" id="primer">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <div className="max-w-3xl">
                        <div className="overline text-civic-600">Primer</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 text-slate-900 break-words">What's a blockchain, and why is it secure?</h2>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                            A <strong>blockchain</strong> is just a list of records (called <em>blocks</em>) where each new entry includes a tamper-evident fingerprint of the one before it. Try to change an old entry and every entry after it stops matching — instantly visible to anyone with a copy of the list. No central authority required.
                        </p>
                    </div>

                    <div className="mt-6 grid sm:grid-cols-3 gap-4">
                        <PrimerCard
                            n="01"
                            icon={Cube}
                            title="A block is a tiny record"
                            body="Each cast vote becomes one block containing the election ID, the choice, a timestamp, and the fingerprint of the previous block. Voter identity is NEVER inside a block."
                        />
                        <PrimerCard
                            n="02"
                            icon={LinkSimple}
                            title="Blocks are chained by math"
                            body="Every block carries the SHA-256 fingerprint of the one before it. Block 47 'depends on' 46, which depends on 45 — all the way back to block 0 (genesis)."
                        />
                        <PrimerCard
                            n="03"
                            icon={Lock}
                            title="Why it's tamper-evident"
                            body="Edit block 47 and its fingerprint changes. Every following block no longer matches. To hide the edit you'd have to recompute every block after it faster than every observer notices — bounded by physics."
                        />
                    </div>

                    <div className="mt-6 grid sm:grid-cols-3 gap-4">
                        <PrimerCard
                            n="04"
                            icon={Eye}
                            title="Everyone can verify"
                            body="The chain is published. Anyone can download it, recompute every hash, and confirm the integrity of every vote without trusting us, the server, or any single operator."
                        />
                        <PrimerCard
                            n="05"
                            icon={FileLock}
                            title="Secrecy by separation"
                            body="A second ledger records who voted (without their choice). The two ledgers reconcile only by totals — voter privacy and public auditability at the same time."
                        />
                        <PrimerCard
                            n="06"
                            icon={Brain}
                            title="No 'trust me' needed"
                            body="Bitcoin, banking SSL, and major government voting research all use these same primitives (SHA-256, hash chains, bcrypt). The math has been audited by the world for decades."
                        />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <div className="overline text-civic-600">Trust model</div>
                            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3 text-slate-900">
                                Two ledgers. <br />One mathematical truth.
                            </h2>
                            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                                E-VoteVerify.com enforces secrecy and integrity by separation. The vote ledger holds anonymous, hash-chained ballots. The roll ledger holds who voted — but never what they chose. Both must reconcile, exactly.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                            <FeatureCard testid="feature-hashchain" icon={LinkSimple} title="Hash-chained ledger" body="Every cast vote is a SHA-256 block whose hash references the previous. Tampering one block breaks the chain — instantly detectable." />
                            <FeatureCard testid="feature-anon" icon={FileLock} title="Anonymous ballots" body="Blocks contain election + candidate, never voter identity. The chain is a public record of choices, not of choosers." />
                            <FeatureCard testid="feature-roll" icon={UserCircleCheck} title="Separate voter roll" body="A second collection records that a voter participated, without storing the candidate. One vote per voter, per election." />
                            <FeatureCard testid="feature-sync" icon={Lightning} title="Total-sync audit" body="Voters who voted = Votes on chain. The two ledgers reconcile by totals — discrepancy is impossible to hide." />
                        </div>
                    </div>
                </div>
            </section>

            {/* Cryptography deep-dive */}
            <section className="border-b border-slate-200 bg-white" id="cryptography">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <div className="overline text-civic-600">Cryptography</div>
                            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3 text-slate-900">
                                How a vote becomes <br />mathematically permanent.
                            </h2>
                            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                                Every ballot is wrapped in a cryptographic envelope. The math doesn't ask you to trust an operator — it lets every observer verify the result themselves, with a calculator and ten lines of code.
                            </p>
                            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                The pieces are well-understood, decades-old building blocks of modern cryptography:
                            </p>
                        </div>
                        <div className="lg:col-span-8 space-y-3">
                            <CryptoCard
                                testid="crypto-sha256"
                                tag="STEP 1"
                                title="SHA-256 — a one-way fingerprint"
                                body={(<>
                                    Every block is run through <span className="hash-mono text-civic-700">SHA-256</span>, the same hash function used by Bitcoin and HTTPS certificates. SHA-256 takes the block's full contents (election id, candidate id, ballot answers, timestamp, previous-block hash, and a nonce) and emits a fixed <span className="hash-mono">64-character</span> hexadecimal fingerprint.
                                    <br /><br />
                                    Change <span className="font-semibold">one bit</span> of the block — flip a "Yes" to a "No", retag a candidate id — and the entire 64-character hash changes unrecognizably. There is no known way to compute the input from the output, and no known way to forge two different blocks with the same hash.
                                </>)}
                            />
                            <CryptoCard
                                testid="crypto-chain"
                                tag="STEP 2"
                                title="The chain — every block references the last"
                                body={(<>
                                    Block <span className="hash-mono">N</span> stores the hash of block <span className="hash-mono">N-1</span> as one of its inputs. So block N's hash <span className="font-semibold">depends</span> on N-1, which depends on N-2, all the way back to the <span className="hash-mono text-civic-700">Genesis block</span>.
                                    <br /><br />
                                    To rewrite history — say, change a vote in block 47 — an attacker would have to recompute the hash of block 47, then 48 (because 47's old hash no longer matches), then 49, then 50… all the way to the latest block, faster than every observer can notice. With SHA-256 that's not just impractical; it's bounded by physics.
                                </>)}
                            />
                            <CryptoCard
                                testid="crypto-separation"
                                tag="STEP 3"
                                title="Two ledgers — secrecy by separation"
                                body={(<>
                                    A real ballot has two questions: <span className="font-semibold">"did this person vote?"</span> (public roll) and <span className="font-semibold">"how did they vote?"</span> (secret choice). E-VoteVerify keeps these on two different MongoDB collections that never join.
                                    <br /><br />
                                    The <span className="hash-mono">blockchain</span> collection stores anonymous choices — election id and candidate id, with no link to who cast them. The <span className="hash-mono">vote_log</span> collection stores who voted, with no link to what they chose. They are reconciled only at the level of totals: <span className="hash-mono text-civic-700">|vote_log| == |blockchain|</span>. Anything else is an audit failure.
                                </>)}
                            />
                            <CryptoCard
                                testid="crypto-receipt"
                                tag="STEP 4"
                                title="Your receipt — a verifiable secret"
                                body={(<>
                                    When you cast a vote, you receive your block's 64-character hash. That hash is <span className="font-semibold">cryptographically tied</span> to your specific choices — but it's also one-way. Anyone can paste it into the <Link to="/explorer" className="text-civic-700 underline-offset-4 hover:underline">Explorer</Link> and confirm "yes, this block is on the chain and these are its choices" — but no one can run the lookup in reverse to figure out which voter you are.
                                    <br /><br />
                                    You're the only person who knows that this particular hash represents <span className="font-semibold">your</span> vote. The math is a key only you hold.
                                </>)}
                            />
                            <CryptoCard
                                testid="crypto-bcrypt"
                                tag="ALSO"
                                title="Voter PIN protection — bcrypt"
                                body={(<>
                                    Voter PINs are never stored in plaintext. They're hashed with <span className="hash-mono text-civic-700">bcrypt</span> (the same one-way function banks use for online passwords) before hitting the database. Even a full database leak does not reveal a single PIN.
                                </>)}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA strip */}
            <section className="bg-civic-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 grid lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-7">
                        <div className="overline text-civic-200">Public access</div>
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl mt-3 break-words">Inspect any block. Recompute any tally.</h2>
                        <p className="mt-3 text-sm text-slate-300 max-w-xl">No login required to verify. The Explorer, Results and Audit pages are open to all citizens.</p>
                    </div>
                    <div className="lg:col-span-5 flex flex-wrap gap-2 sm:gap-3 lg:justify-end">
                        <Link to="/explorer"><Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-civic-900" data-testid="cta-explorer"><Stack size={16} className="mr-2" /> Explorer</Button></Link>
                        <Link to="/results"><Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-civic-900" data-testid="cta-results"><ChartBar size={16} className="mr-2" /> Results</Button></Link>
                        <Link to="/audit"><Button size="lg" className="bg-white text-civic-900 hover:bg-slate-100" data-testid="cta-audit"><MagnifyingGlass size={16} className="mr-2" weight="bold" /> Audit</Button></Link>
                    </div>
                </div>
            </section>

            {/* Sales / Licensing inquiry strip */}
            <section className="bg-slate-100 border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="overline text-civic-700">Bring E-VoteVerify to your organization</div>
                        <h2 className="font-heading font-bold text-xl sm:text-2xl mt-2 text-slate-900 break-words">Interested in a license or a hosted deployment?</h2>
                        <p className="mt-2 text-sm text-slate-600 max-w-2xl">Counties, HOAs, school boards, universities, unions, family groups — anyone running an election with more than a handful of participants can license E-VoteVerify. Tell us about your use case and our sales team will get back to you.</p>
                    </div>
                    <div className="shrink-0">
                        <Button
                            size="lg"
                            onClick={() => setSalesOpen(true)}
                            className="bg-civic-600 hover:bg-civic-700 text-white"
                            data-testid="sales-inquiry-open-btn"
                        >
                            <EnvelopeSimple size={16} className="mr-2" weight="fill" />
                            Inquire about Sales / Licensing
                        </Button>
                    </div>
                </div>
            </section>

            <SalesInquiryDialog open={salesOpen} onOpenChange={setSalesOpen} />
        </div>
    );
}

